import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import type { FastColumn } from "../types";

type ValueType = FastColumn["value_type"];
export type ColumnDraft = { label: string; description: string; value_type: ValueType; options: string[] };

export const TYPES: { value: ValueType; label: string; hint: string }[] = [
  { value: "text", label: "Text", hint: "The source's words, as written." },
  { value: "number", label: "Number", hint: "The number in the source's words; the quote keeps its unit." },
  { value: "count", label: "Count", hint: "A whole number, e.g. participants." },
  { value: "yes_no", label: "Yes / no", hint: "Yes or no, judged from the quoted sentence." },
  { value: "category", label: "Category", hint: "One of your options, judged from the quoted sentence." },
  { value: "image", label: "Image", hint: "A figure from each paper, found by its caption and the text in the image." },
];

export const parseOptions = (text: string) => [...new Set(text.split(/[,\n]/).map((o) => o.trim()).filter(Boolean))].slice(0, 20);

/**
 * The space above the table where a column is added, or an existing column is read, edited or
 * deleted. Saving a changed column answers it again for every row.
 */
export const ColumnPanel = ({
  initial,
  fixed,
  busy,
  onSave,
  onDelete,
  onClose,
}: {
  /** The column being edited; none when adding. */
  initial: ColumnDraft | null;
  /** Each paper's rows are split on the column: it can only be renamed. */
  fixed?: boolean;
  busy: boolean;
  onSave: (draft: ColumnDraft) => void;
  onDelete?: () => void;
  onClose: () => void;
}) => {
  const [label, setLabel] = useState(initial?.label ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [valueType, setValueType] = useState<ValueType>(initial?.value_type ?? "text");
  const [optionsText, setOptionsText] = useState((initial?.options ?? []).join(", "));
  const options = parseOptions(optionsText);
  const optionsOk = valueType !== "category" || options.length >= 2;
  const changed =
    !initial ||
    label.trim() !== initial.label ||
    description.trim() !== initial.description ||
    valueType !== initial.value_type ||
    (valueType === "category" && options.join("\n") !== initial.options.join("\n"));
  const canSave = !busy && !!label.trim() && changed && optionsOk;

  const save = () => {
    if (canSave)
      onSave({ label: label.trim(), description: description.trim(), value_type: valueType, options: valueType === "category" ? options : [] });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      className="mb-3 rounded-[3px] border border-zinc-200 bg-zinc-50/60 p-3 animate-in fade-in"
      aria-label={initial ? `Column ${initial.label}` : "Add column"}
    >
      <div className="flex items-start gap-3">
        <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[220px_1fr]">
          <div className="grid gap-2">
            <Input
              autoFocus={!initial}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              placeholder="Column name, e.g. Country"
              aria-label="Column name"
              className="text-sm"
            />
            <label className="font-ui flex items-center gap-2 text-xs text-zinc-500">
              Type
              <select
                value={valueType}
                onChange={(e) => setValueType(e.target.value as ValueType)}
                disabled={fixed}
                className="flex-1 rounded-[3px] border border-zinc-300 bg-white py-1 pl-2 pr-7 text-xs text-zinc-800 focus:ring-2 focus:ring-zinc-900 disabled:opacity-60"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-2">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={600}
              rows={2}
              disabled={fixed}
              placeholder={
                valueType === "yes_no"
                  ? "A yes/no question for each source, e.g. Was the trial double-blind?"
                  : valueType === "image"
                  ? "What the figure shows, e.g. Kaplan-Meier overall survival curve"
                  : "What to find in each source, e.g. country where the study was conducted (e.g. Finland)"
              }
              aria-label="Column description"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  save();
                }
              }}
              className="block w-full resize-none rounded-[3px] border border-zinc-300 bg-white px-3 py-1.5 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 disabled:opacity-60"
            />
            {valueType === "category" && (
              <Input
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                disabled={fixed}
                placeholder="Options, separated by commas, e.g. Placebo, Active comparator, None"
                aria-label="Category options"
                className="text-sm"
              />
            )}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="mt-1.5 text-zinc-400 hover:text-zinc-900">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="font-ui mt-2 flex items-center justify-between gap-3 text-xs text-zinc-500">
        <span>
          {fixed
            ? "Each document's rows are split on this column (one row per drug and dose), so only its name can change."
            : !optionsOk
              ? "A category column needs at least two options."
              : `${TYPES.find((t) => t.value === valueType)?.hint} ${
                  initial ? "Saving a change finds the values again for every row." : "Values are found in the passages already read for every row."
                }`}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {initial && onDelete && !fixed && (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete
            </Button>
          )}
          <Button type="submit" size="sm" disabled={!canSave}>
            {fixed ? "Rename" : initial ? "Save and rerun" : "Add column"}
          </Button>
        </div>
      </div>
    </form>
  );
};
